import Table from "components/Table/Table";
import { commonColumnProps, OFFSET, PAGE_SIZE } from "constants/TableConstants";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { useNavigate } from "react-router-dom";
import { setIsLoading } from "reducers/main";
import { useGetBulkDownloadRequestsQuery } from "services/documents-api";
import { DATE_AND_TIME_FORMAT } from "utils/date-utils";
import { useQuery } from "utils/hooks";
import { useDispatch } from "utils/redux";
import { GLOBAL_SEARCH_PARAM } from "utils/useUrlStore";
import BulkDownloadsPageContainer from "./BulkDownloadsPageContainer";

type BulkDownloadRowType = {
  id: number;
  status: string;
  source: string;
  total_documents: number;
  processed_documents: number;
  created: string;
  expires_at: string;
};

const DOWNLOAD_COLUMNS = [
  { key: "id", name: "Request #", ...commonColumnProps },
  { key: "source", name: "Source", ...commonColumnProps },
  { key: "total_documents", name: "Total Docs", ...commonColumnProps },
  { key: "processed_documents", name: "Processed", ...commonColumnProps },
  { key: "status", name: "Status", ...commonColumnProps },
  { key: "created", name: "Created", ...commonColumnProps },
  { key: "expires_at", name: "Expires", ...commonColumnProps },
];

const BulkDownloadsTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useQuery();
  const [page, setPage] = useState(1);

  const {
    data: downloads = { results: [], count: 0 },
    isLoading,
    isFetching,
  } = useGetBulkDownloadRequestsQuery(
    {
      limit: PAGE_SIZE.toString(),
      offset: OFFSET(page).toString(),
      searchTerm: query.get(GLOBAL_SEARCH_PARAM) || undefined,
    },
    {
      skip: false,
    },
  );

  const handleCellClick = (
    args: CellClickArgs<BulkDownloadRowType, unknown>,
  ) => {
    navigate(`/bulk-download/${args.row.id}`);
  };

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const rows =
    downloads.results?.map((download) => {
      let source = "";
      if (download.file) {
        source = `File #${download.file}`;
      } else if (download.client) {
        source = `Client #${download.client}`;
      } else if (download.claim) {
        source = `Claim #${download.claim}`;
      }

      return {
        id: download.id,
        status:
          download.status.charAt(0).toUpperCase() + download.status.slice(1),
        source,
        total_documents: download.total_documents,
        processed_documents: download.processed_documents,
        created: format(parseISO(download.created), DATE_AND_TIME_FORMAT),
        expires_at: download.expires_at
          ? format(parseISO(download.expires_at), DATE_AND_TIME_FORMAT)
          : "N/A",
      };
    }) || [];

  return (
    <BulkDownloadsPageContainer>
      {!isLoading && downloads.results?.length === 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "400px",
            gap: "16px",
            color: "#666",
          }}
        >
          <span className="material-icons" style={{ fontSize: "64px" }}>
            download
          </span>
          <h3 style={{ margin: 0 }}>No bulk downloads yet</h3>
          <p style={{ margin: 0 }}>Bulk download requests will appear here</p>
        </div>
      ) : (
        <Table
          columns={DOWNLOAD_COLUMNS}
          rows={rows}
          rowKeyGetter={(row: BulkDownloadRowType) => row.id}
          onCellClick={handleCellClick}
          isLoading={isFetching}
          fullHeight
          data-testid="bulk-downloads-table"
          pagination={{
            pageSize: PAGE_SIZE,
            pageIndex: page,
            rowCount: downloads.count,
            onPageChange: (pageIndex) => setPage(pageIndex),
            isLoading: isFetching,
          }}
        />
      )}
    </BulkDownloadsPageContainer>
  );
};

export default BulkDownloadsTable;
